import { type StyleProps, Text } from '@chakra-ui/react'
import DOMPurify from 'dompurify'
import React, { useCallback, useMemo } from 'react'
import { createEditor } from 'slate'
import {
  Editable,
  type RenderElementProps,
  type RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react'

import logger from '@/utils/logger'

import deserializeRichText from '@/client/services/richText/deserializeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'

import { withErrorBoundary } from '@/components/Worlds/UI/ErrorBoundary'

import LatexContainer from '../LatexContainer/LatexContainer'
import RichTextElement from './internal/RichTextElement'
import RichTextLeaf from './internal/RichTextLeaf'

interface Props {
  content: SerializedRichText | null
  styleProps?: StyleProps
}

function RichTextSlate({
  content,
  styleProps,
}: {
  content: SerializedRichText | null
  styleProps?: StyleProps
}): React.ReactElement {
  const editor = useMemo(() => withReact(createEditor()), [])
  const renderElement = React.useCallback(
    (props: RenderElementProps) => <RichTextElement {...props} styleProps={styleProps} />,
    [styleProps]
  )
  const renderLeaf = React.useCallback(
    (props: RenderLeafProps) => <RichTextLeaf {...props} styleProps={styleProps} />,
    [styleProps]
  )
  const value = useMemo(() => deserializeRichText(content), [content])
  editor.children = value

  return (
    <Slate editor={editor} value={value}>
      <LatexContainer>
        <Editable renderElement={renderElement} renderLeaf={renderLeaf} readOnly />
      </LatexContainer>
    </Slate>
  )
}

const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true, svg: true, svgFilters: true, mathMl: true },
  ADD_TAGS: ['mfenced', 'use'],
  ADD_ATTR: ['open', 'close', 'separators', 'xlink:href'],
}

const SHADOW_DOM_STYLES = `<style>
  :host {
    display: block;
    max-width: 100%;
  }
  * {
    overflow-wrap: break-word !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    max-width: 100% !important;
  }
  p { margin: 0; }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap !important;
    border: 0;
  }
</style>`

function polyfillMfenced(root: DocumentFragment | ShadowRoot) {
  const mfencedElements = root.querySelectorAll('mfenced')
  for (const el of mfencedElements) {
    if (el.parentNode == null) {
      return
    }

    const open = el.getAttribute('open') ?? '('
    const close = el.getAttribute('close') ?? ')'
    const separators = el.getAttribute('separators') ?? ','

    const mathNs = 'http://www.w3.org/1998/Math/MathML'
    const openMo = document.createElementNS(mathNs, 'mo')
    openMo.textContent = open
    const closeMo = document.createElementNS(mathNs, 'mo')
    closeMo.textContent = close

    el.before(openMo)
    const children = Array.from(el.childNodes)
    for (let i = 0; i < children.length; i += 1) {
      el.before(children[i])
      if (i < children.length - 1 && separators.length > 0) {
        const sepMo = document.createElementNS(mathNs, 'mo')
        sepMo.textContent = separators[Math.min(i, separators.length - 1)]
        el.before(sepMo)
      }
    }
    el.before(closeMo)
    el.remove()
  }
}

function RichTextShadowDom({ content }: { content: string }): React.ReactElement {
  const attachShadow = useCallback(
    (node: HTMLDivElement | null) => {
      if (node == null) {
        return
      }
      const sanitized = SHADOW_DOM_STYLES + DOMPurify.sanitize(content, SANITIZE_CONFIG)
      const shadow = node.shadowRoot ?? node.attachShadow({ mode: 'open' })
      shadow.innerHTML = sanitized
      polyfillMfenced(shadow)
    },
    [content]
  )

  return <div ref={attachShadow} />
}

function RichTextInternal({ content, styleProps }: Props): React.ReactElement {
  if (
    content == null ||
    content.serialization_method === 'slate_json_v1' ||
    content.serialization_method === 'plain_text'
  ) {
    return <RichTextSlate content={content} styleProps={styleProps} />
  }

  if (content.serialization_method === 'raw_html') {
    return <RichTextShadowDom content={content.content} />
  }

  logger.error('RichText: unknown serialization method', { content })

  return <Text color="fg.muted">Oops! There was an error displaying this content.</Text>
}

const RichText = withErrorBoundary(RichTextInternal, () => (
  <Text color="fg.muted">Oops! There was an error displaying this content.</Text>
))

export default RichText
